import axios from "axios";
import config from "../config";
import { ApiSuccess } from "./api_helper";
import { PaginatedMeta } from "./audience_helper";

const apiBase = config.api.API_URL;

/** Constantes do pack +10k (espelho DOC 10 § 6.4 / config API). */
export const QUOTA_PACK_EMAILS = 10000;
export const QUOTA_PACK_PRICE_CENTS = 3900;
export const QUOTA_PACK_USD_PRICE_CENTS = 500;
export const QUOTA_PACK_MAX_PER_PERIOD = 3;

export type SubscriptionPlan = {
  id: number;
  name: string;
  slug: string;
  description?: string | null;
  email_quota: number;
  base_price_cents?: number;
  is_trial_plan?: boolean;
  is_active?: boolean;
  currency?: string;
  billing_interval?: string;
};

/** Promo ativa na assinatura (LAC-089) — null se completed/ausente. */
export type ActiveCoupon = {
  code: string;
  status: string;
  duration_type: string;
  duration_cycles: number | null;
  cycles_remaining: number | null;
  discount_percent_bps: number;
  amount_cents: number;
  applied_at: string | null;
};

export type Subscription = {
  id: number;
  status: string;
  currency: string;
  /** self_serve | brdominio — esconde checkout/pack quando externo (LAC-053). */
  billing_channel?: "self_serve" | "brdominio" | string;
  emails_used_in_period: number;
  /** Packs +10k já creditados neste ciclo (0–3). */
  quota_packs_in_period?: number;
  /** Cota efetiva = plano + packs×10k (API). */
  email_quota_effective?: number;
  current_period_start: string | null;
  current_period_end: string | null;
  /** UTC — início do grace past_due (countdown até pausa). */
  dunning_entered_at?: string | null;
  /** Dias restantes até soft-lock (só past_due). */
  days_until_suspend?: number | null;
  /** LAC-056: pedido de cancelamento no fim do ciclo. */
  cancel_requested_at?: string | null;
  /** Dias até current_period_end (só pending_cancel). */
  days_until_period_end?: number | null;
  /** LAC-055: cartão tokenizado no gateway. */
  has_saved_card?: boolean;
  saved_card?: {
    last4?: string | null;
    brand?: string | null;
    brand_label?: string | null;
  } | null;
  plan: SubscriptionPlan | null;
  /** Cupom ainda em vigor — copy leigo em Plano e cota (LAC-089). */
  active_coupon?: ActiveCoupon | null;
};

export type SaasBillingInvoice = {
  id: number;
  status: string;
  currency: string;
  amount_cents: number;
  kind?: string | null;
  external_invoice_id?: string | null;
  period_start?: string | null;
  period_end?: string | null;
  paid_at?: string | null;
  created_at?: string | null;
  plan_id?: number | null;
  gateway?: "paypal" | "mercadopago" | string;
  gateway_label?: string | null;
  /** Código do cupom aplicado nesta fatura (LAC-093). */
  coupon_code?: string | null;
};

export type CheckoutResult = {
  subscription: Subscription;
  invoice: SaasBillingInvoice | null;
  /** PayPal approval_url quando checkout USD exige aprovação externa. */
  approval_url?: string | null;
};

/** Gateways disponíveis para o tenant (F2 — USD PayPal). */
export type PublicBillingGateway = {
  gateway: string;
  ready: boolean;
  /** PayPal client_id público — só quando ready=true. */
  paypal_client_id?: string | null;
  /** PayPal sandbox | live — só quando ready=true. */
  paypal_mode?: "sandbox" | "live" | null;
};

export type PublicBillingGateways = {
  default_currency: "BRL" | "USD";
  brl: PublicBillingGateway;
  usd: PublicBillingGateway;
};

type InvoicesListResponse = ApiSuccess<SaasBillingInvoice[]> & {
  meta: PaginatedMeta;
};

/**
 * Token mock do Fake gateway (BILLING_GATEWAY=fake).
 * Só usar quando não há REACT_APP_MERCADOPAGO_PUBLIC_KEY (homolog local sem Brick).
 */
export const FAKE_CARD_TOKEN_ID = "tok_fake";

/** Cota do ciclo: preferir efetiva (plano + packs); fallback = plano. */
export const effectiveQuota = (sub: Subscription | null | undefined): number => {
  if (!sub) return 0;
  if (typeof sub.email_quota_effective === "number") {
    return sub.email_quota_effective;
  }
  return sub.plan?.email_quota ?? 0;
};

/** % leigo a partir de basis points (10000 = 100%). */
export const discountPercentFromBps = (bps: number): number =>
  Math.round((bps / 100) * 100) / 100;

/** GET /api/v1/subscription — plano + cota do ciclo atual. */
export const getSubscription = (): Promise<ApiSuccess<Subscription>> => {
  return axios.get(apiBase + "/subscription");
};

/** Uso diário de cota no ciclo — LAC-020 (painel; EXCLUDE portal). */
export type SubscriptionUsageSeriesPoint = {
  date: string;
  used: number;
  campaign_sent: number;
  api_sent: number;
};

export type SubscriptionUsageSeries = {
  period_start: string | null;
  period_end: string | null;
  emails_used_in_period: number;
  email_quota_effective: number;
  /** ledger = quota_usage_days; reconstructed = campanha+API. */
  source?: "ledger" | "reconstructed" | string;
  series: SubscriptionUsageSeriesPoint[];
};

export const getSubscriptionUsageSeries = (): Promise<
  ApiSuccess<SubscriptionUsageSeries>
> => {
  return axios.get(apiBase + "/subscription/usage-series");
};

/** GET /api/v1/plans — catálogo público ativo (upgrade). */
export const getPublicPlans = (
  currency = "BRL"
): Promise<ApiSuccess<SubscriptionPlan[]>> => {
  return axios.get(apiBase + "/plans", { params: { currency } });
};

/** GET /api/v1/public/billing-gateways — USD/BRL ready, PayPal client_id (F2). */
export const getPublicBillingGateways = (): Promise<
  ApiSuccess<PublicBillingGateways>
> => {
  return axios.get(apiBase + "/public/billing-gateways");
};

/** GET /api/v1/subscription/invoices — faturas SaaS do tenant. */
export const getSubscriptionInvoices = (params?: {
  page?: number;
  per_page?: number;
}): Promise<InvoicesListResponse> => {
  return axios.get(apiBase + "/subscription/invoices", {
    params: { per_page: params?.per_page || 25, page: params?.page },
  });
};

/**
 * Recibo PDF (LAC-054) — download blob JWT.
 */
export const downloadInvoiceReceipt = async (
  invoiceId: number
): Promise<void> => {
  const data = await axios.get(
    apiBase + "/subscription/invoices/" + invoiceId + "/pdf",
    { responseType: "blob" }
  );

  const blob =
    data instanceof Blob
      ? data
      : new Blob([data as unknown as BlobPart], {
          type: "application/pdf",
        });

  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `recibo-${invoiceId}.pdf`;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
};

/**
 * Recibo PDF inline — abre em nova aba (mesmo endpoint com ?inline=1).
 */
export const openInvoiceReceipt = async (
  invoiceId: number
): Promise<void> => {
  const data = await axios.get(
    apiBase + "/subscription/invoices/" + invoiceId + "/pdf",
    { responseType: "blob", params: { inline: 1 } }
  );

  const blob =
    data instanceof Blob
      ? data
      : new Blob([data as unknown as BlobPart], {
          type: "application/pdf",
        });

  const url = URL.createObjectURL(blob);
  window.open(url, "_blank", "noopener,noreferrer");
  // Revoga depois — browser precisa do blob um instante ao abrir.
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
};

/** Reenvia e-mail com PDF do recibo (fatura paid). */
export const resendInvoiceReceipt = (
  invoiceId: number
): Promise<ApiSuccess<null>> => {
  return axios.post(
    apiBase + "/subscription/invoices/" + invoiceId + "/resend-receipt"
  );
};

/** POST /api/v1/subscription/checkout — troca/contrata plano pago. */
export const checkoutSubscription = (payload: {
  plan_id: number;
  card_token_id: string;
  coupon_code?: string | null;
}): Promise<ApiSuccess<CheckoutResult>> => {
  return axios.post(apiBase + "/subscription/checkout", payload);
};

/** POST /api/v1/subscription/paypal/prepare — plan_id PayPal p/ SDK Buttons. */
export const preparePayPalCheckout = (payload: {
  plan_id: number;
  coupon_code?: string | null;
}): Promise<
  ApiSuccess<{
    paypal_plan_id: string;
    client_id: string;
    amount_cents: number;
    currency: string;
  }>
> => {
  return axios.post(apiBase + "/subscription/paypal/prepare", payload);
};

/** Preview cupom — não consome estoque. */
export type CouponPreview = {
  code: string;
  discount_percent_bps: number;
  duration_type: string;
  duration_cycles: number | null;
  plan_id: number;
  currency: string;
  subtotal_cents: number;
  discount_cents: number;
  total_cents: number;
};

export const validateCheckoutCoupon = (payload: {
  code: string;
  plan_id: number;
}): Promise<ApiSuccess<CouponPreview>> => {
  return axios.post(
    apiBase + "/subscription/checkout/validate-coupon",
    payload
  );
};

/** POST /api/v1/subscription/cancel — now | period_end (LAC-056). */
export const cancelSubscription = (payload?: {
  when?: "now" | "period_end";
}): Promise<ApiSuccess<Subscription>> => {
  return axios.post(apiBase + "/subscription/cancel", payload ?? {});
};

/** POST /api/v1/subscription/resume — desfaz pending_cancel. */
export const resumeSubscription = (): Promise<ApiSuccess<Subscription>> => {
  return axios.post(apiBase + "/subscription/resume");
};

/** POST /api/v1/subscription/payment-method — troca cartão da recorrência. */
export const updatePaymentMethod = (payload: {
  card_token_id: string;
}): Promise<ApiSuccess<Subscription>> => {
  return axios.post(apiBase + "/subscription/payment-method", payload);
};

/** POST /api/v1/subscription/add-on-quota — pack +10k (token ou cartão salvo). */
export const purchaseQuotaPack = (payload: {
  card_token_id?: string;
  use_saved_card?: boolean;
}): Promise<ApiSuccess<CheckoutResult>> => {
  return axios.post(apiBase + "/subscription/add-on-quota", payload);
};
